"use client"

import { useState, useEffect, useRef } from "react"
import { useToast } from "@/hooks/use-toast"
import { Button, Input, Tag, Icon } from "animal-island-ui"
import { ConfigSwitch as Switch } from "@/components/config-switch"
import item010 from "animal-island-ui/items/item-010.png"
import item023 from "animal-island-ui/items/item-023.png"
import item047 from "animal-island-ui/items/item-047.png"
import item061 from "animal-island-ui/items/item-061.png"
import item089 from "animal-island-ui/items/item-089.png"
import item105 from "animal-island-ui/items/item-105.png"
import item131 from "animal-island-ui/items/item-131.png"
import item157 from "animal-island-ui/items/item-157.png"
import item173 from "animal-island-ui/items/item-173.png"
import { apiRequestWithAuth, getStoredToken, isTokenValid } from "@/lib/api-config"
import { useAuth } from "@/contexts/auth-context"
import { DashboardLayout } from "@/components/dashboard-layout"
import { ConfigSavebar } from "@/components/config-savebar"
import { FightPlanEditor } from "@/components/fight-plan-editor"
import { initializeFightPlan, validateFightPlan } from "@/lib/fight-plan"
import { Divider } from "animal-island-ui"
import { User, CalendarBlank as Calendar, Lightning as Zap, ArrowsClockwise as RefreshCw, HardDrives as Server, Shield, List, X, CaretDown as ChevronDown } from "@phosphor-icons/react"

export interface UserAccountInfo {
  id: number
  name: string
  account: string
  password: string
  freeze: number
  server: number
  taskType: string
  config: {
    daily: {
      fight: Array<{
        level: string
        num: number
      }>
      sanity: {
        drug: number
        stone: number
      }
      mail: boolean
      offer: {
        enable: boolean
        car: boolean
        star4: boolean
        star5: boolean
        star6: boolean
        other: boolean
      }
      friend: boolean
      infrastructure: {
        harvest: boolean
        shift: boolean
        acceleration: boolean
        communication: boolean
        deputy: boolean
      }
      credit: boolean
      task: boolean
      activity: boolean
      cultivation: boolean
      cultivation_plan: any[]
      fight_enable: boolean
    }
    rogue: {
      operator: {
        index: number
        num: number
        skill: number
      }
      level: number
      coin: number
      type: number
      skip: {
        coin: boolean
        beast: boolean
        daily: boolean
        sensitive: boolean
        illusion: boolean
        survive: boolean
      }
    }
  }
  active: {
    monday: { enable: boolean; detail: any[] }
    tuesday: { enable: boolean; detail: any[] }
    wednesday: { enable: boolean; detail: any[] }
    thursday: { enable: boolean; detail: any[] }
    friday: { enable: boolean; detail: any[] }
    saturday: { enable: boolean; detail: any[] }
    sunday: { enable: boolean; detail: any[] }
  }
  notice: {
    wxUID: { text: string; enable: boolean }
    qq: { text: string; enable: boolean }
    mail: { text: string; enable: boolean }
  }
  refresh: number
  agent: string | null
  createTime: string
  updateTime: string
  expireTime: string
  delete: number
  blimitDevice: any[]
}

const activeDayNames: Record<string, string> = {
  monday: "周一",
  tuesday: "周二",
  wednesday: "周三",
  thursday: "周四",
  friday: "周五",
  saturday: "周六",
  sunday: "周日",
}

const defaultActiveSlot = "08:00-10:00"

function normalizeActiveSlots(detail: unknown): string[] {
  if (!Array.isArray(detail)) return []
  return detail.filter((slot): slot is string =>
    typeof slot === "string" && /^([01]\d|2[0-3]):[0-5]\d-([01]\d|2[0-3]):[0-5]\d$/.test(slot)
  )
}

function splitActiveSlot(slot: string): [string, string] {
  const [start = "08:00", end = "10:00"] = slot.split("-")
  return [start, end]
}

function ActiveTimeModeSelect({day, value, disabled, onChange}: {day: string; value: string; disabled?: boolean; onChange: (value: string) => void}) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const options = [
    {value: "all", label: "全天允许"},
    {value: "custom", label: "自定义时段"},
    {value: "none", label: "当天不允许"},
  ]
  const selected = options.find(option => option.value === value) ?? options[0]

  useEffect(() => {
    const handlePointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener("pointerdown", handlePointerDown)
    return () => document.removeEventListener("pointerdown", handlePointerDown)
  }, [])

  return <div className="active-time-mode" ref={rootRef}>
    <button type="button" className="active-time-mode-trigger" aria-haspopup="listbox" aria-expanded={open} onClick={() => setOpen(current => !current)} onKeyDown={event => { if (event.key === "Escape") setOpen(false) }} disabled={disabled}>
      <span>{selected.label}</span><ChevronDown size={15} aria-hidden="true" />
    </button>
    {open && <div className="active-time-mode-menu" role="listbox" aria-label={`${activeDayNames[day]}允许上号模式`}>
      {options.map(option => <button type="button" role="option" aria-selected={option.value === value} className="active-time-mode-option" key={option.value} onClick={() => { onChange(option.value); setOpen(false) }}>
        <span>{option.label}</span>{option.value === value && <span className="active-time-check" aria-hidden="true">✓</span>}
      </button>)}
    </div>}
  </div>
}

export default function UserAccount() {
  const { token: contextToken } = useAuth()
  const { toast } = useToast()
  const [accountInfo, setAccountInfo] = useState<UserAccountInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [, setEditAccount] = useState("")
  const [updateLoading, setUpdateLoading] = useState(false)
  const [sanity, setSanity] = useState<string>("")

  const [baseConfig, setBaseConfig] = useState<{
    friend: boolean
    credit: boolean
    mail: boolean
    activity: boolean
    task: boolean
    fight_enable: boolean
    fight: Array<{ level: string; num: number }>
    infrastructure: {
      shift: boolean
      harvest: boolean
      acceleration: boolean
      communication: boolean
      deputy: boolean
      [key: string]: boolean
    }
    wxUIDText: string | null
    wxUIDEnable: boolean | null
    qqText: string | null
    qqEnable: boolean | null
    mailText: string | null
    mailEnable: boolean | null
    mondayEnable: boolean | null
    tuesdayEnable: boolean | null
    wednesdayEnable: boolean | null
    thursdayEnable: boolean | null
    fridayEnable: boolean | null
    saturdayEnable: boolean | null
    sundayEnable: boolean | null
    activeDetails: Record<string, string[]>
    offer: {
      enable: boolean;
      car: boolean;
      other: boolean;
      star4: boolean;
      star5: boolean;
      star6: boolean;
      [key: string]: boolean;
    } | null;
    sanityStone?: number
    sanityDrug?: number
    [key: string]: any
  }>(
    {
      friend: false,
      credit: false,
      mail: false,
      activity: false,
      task: false,
      fight_enable: false,
      fight: initializeFightPlan(),
      infrastructure: {
        shift: false,
        harvest: false,
        acceleration: false,
        communication: false,
        deputy: false,
      },
      wxUIDText: null,
      wxUIDEnable: null,
      qqText: null,
      qqEnable: null,
      mailText: null,
      mailEnable: null,
      mondayEnable: true,
      tuesdayEnable: true,
      wednesdayEnable: true,
      thursdayEnable: true,
      fridayEnable: true,
      saturdayEnable: true,
      sundayEnable: true,
      activeDetails: {},
      offer: null,
      sanityStone: 0,
      sanityDrug: 0,
    }
  )

  const getToken = () => contextToken || getStoredToken()

  const fetchAccountInfo = async () => {
    const token = getToken()
    if (!token || !isTokenValid(token)) {
      toast({ variant: "destructive", title: "认证失败", description: "请重新登录" })
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      const result = await apiRequestWithAuth("/showMyAccount", token, { method: "GET" })
      if (result.code === 200) {
        const data = result.data as UserAccountInfo
        setAccountInfo(data)
        setEditAccount(data?.account || "")
        setBaseConfig(prev => ({
          ...prev,
          wxUIDText: data.notice?.wxUID?.text || null,
          wxUIDEnable: data.notice?.wxUID?.enable || null,
          qqText: data.notice?.qq?.text || null,
          qqEnable: data.notice?.qq?.enable || null,
          mailText: data.notice?.mail?.text || null,
          mailEnable: data.notice?.mail?.enable || null,
          mondayEnable: data.active?.monday?.enable ?? true,
          tuesdayEnable: data.active?.tuesday?.enable ?? true,
          wednesdayEnable: data.active?.wednesday?.enable ?? true,
          thursdayEnable: data.active?.thursday?.enable ?? true,
          fridayEnable: data.active?.friday?.enable ?? true,
          saturdayEnable: data.active?.saturday?.enable ?? true,
          sundayEnable: data.active?.sunday?.enable ?? true,
          activeDetails: Object.fromEntries(Object.entries(data.active ?? {}).map(([day, config]) => [day, normalizeActiveSlots(config.detail)])),
          offer: data.config?.daily?.offer ?? null,
          sanityStone: data.config?.daily?.sanity?.stone ?? 0,
          sanityDrug: data.config?.daily?.sanity?.drug ?? 0,
        }))
      } else {
        toast({ variant: "destructive", title: "获取失败", description: result.msg })
      }
    } catch (error) {
      toast({ variant: "destructive", title: "网络错误", description: error instanceof Error ? error.message : "获取失败" })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (accountInfo) {
      setBaseConfig({
        friend: accountInfo.config?.daily?.friend ?? false,
        credit: accountInfo.config?.daily?.credit ?? false,
        mail: accountInfo.config?.daily?.mail ?? false,
        activity: accountInfo.config?.daily?.activity ?? false,
        task: accountInfo.config?.daily?.task ?? false,
        fight_enable: accountInfo.config?.daily?.fight_enable ?? false,
        fight: initializeFightPlan(accountInfo.config?.daily?.fight),
        infrastructure: accountInfo.config?.daily?.infrastructure ?? {
          shift: false,
          harvest: false,
          acceleration: false,
          communication: false,
          deputy: false,
        },
        wxUIDText: accountInfo.notice?.wxUID?.text || null,
        wxUIDEnable: accountInfo.notice?.wxUID?.enable || null,
        qqText: accountInfo.notice?.qq?.text || null,
        qqEnable: accountInfo.notice?.qq?.enable || null,
        mailText: accountInfo.notice?.mail?.text || null,
        mailEnable: accountInfo.notice?.mail?.enable || null,
        mondayEnable: accountInfo.active?.monday?.enable ?? true,
        tuesdayEnable: accountInfo.active?.tuesday?.enable ?? true,
        wednesdayEnable: accountInfo.active?.wednesday?.enable ?? true,
        thursdayEnable: accountInfo.active?.thursday?.enable ?? true,
        fridayEnable: accountInfo.active?.friday?.enable ?? true,
        saturdayEnable: accountInfo.active?.saturday?.enable ?? true,
        sundayEnable: accountInfo.active?.sunday?.enable ?? true,
        activeDetails: Object.fromEntries(Object.entries(accountInfo.active ?? {}).map(([day, config]) => [day, normalizeActiveSlots(config.detail)])),
        offer: accountInfo.config?.daily?.offer ?? null,
        sanityStone: accountInfo.config?.daily?.sanity?.stone ?? 0,
        sanityDrug: accountInfo.config?.daily?.sanity?.drug ?? 0,
      })
    }
  }, [accountInfo])

  const handleSaveBaseConfig = async () => {
    const token = getToken()
    if (!token || !isTokenValid(token) || !accountInfo) return
    const fightError = validateFightPlan(baseConfig.fight)
    if (fightError) {
      toast({variant:"destructive",title:"作战配置未保存",description:fightError})
      return
    }
    setUpdateLoading(true)
    try {
      const newConfig = {
        ...accountInfo.config,
        daily: {
          ...accountInfo.config.daily,
          ...baseConfig,
          fight: baseConfig.fight.map(item => ({...item, level:item.level.trim()})),
          sanity: {
            ...accountInfo.config.daily.sanity,
            stone: baseConfig.sanityStone ?? accountInfo.config.daily.sanity?.stone ?? 0,
            drug: baseConfig.sanityDrug ?? accountInfo.config.daily.sanity?.drug ?? 0,
          },
        },
      }
      const newNotice = {
        wxUID: {
          text: baseConfig.wxUIDText ?? accountInfo.notice.wxUID.text,
          enable: baseConfig.wxUIDEnable ?? accountInfo.notice.wxUID.enable,
        },
        qq: {
          text: baseConfig.qqText ?? accountInfo.notice.qq.text,
          enable: baseConfig.qqEnable ?? accountInfo.notice.qq.enable,
        },
        mail: {
          text: baseConfig.mailText ?? accountInfo.notice.mail.text,
          enable: baseConfig.mailEnable ?? accountInfo.notice.mail.enable,
        },
      }
      const newActive = {
        ...Object.fromEntries(Object.entries(accountInfo.active).map(([day, config]) => [day, {
          ...config,
          enable: baseConfig[`${day}Enable`] ?? config.enable,
          detail: normalizeActiveSlots(baseConfig.activeDetails?.[day] ?? config.detail),
        }])),
      }
      const result = await apiRequestWithAuth("/updateMyAccount", token, {
        method: "POST",
        body: JSON.stringify({
          config: newConfig,
          notice: newNotice,
          active: newActive,
        }),
        headers: { "Content-Type": "application/json" },
      })
      if (result.code === 200) {
        toast({ variant: "success", title: "保存成功", description: result.msg })
        await fetchAccountInfo()
      } else {
        toast({ variant: "destructive", title: "保存失败", description: result.msg })
      }
    } catch (error) {
      toast({ variant: "destructive", title: "网络错误", description: error instanceof Error ? error.message : "保存失败" })
    } finally {
      setUpdateLoading(false)
    }
  }

  useEffect(() => {
    fetchAccountInfo()
    fetchSanity()
  }, [])

  const fetchSanity = async () => {
    const token = getToken()
    if (!token || !isTokenValid(token)) return
    try {
      const result = await apiRequestWithAuth("/showMySan", token, { method: "GET" })
      if (result.code === 200) {
        setSanity(String(result.data))
      } else {
        setSanity("-")
      }
    } catch {
      setSanity("-")
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString("zh-CN")
  }
  const isExpired = (expireTime: string) => {
    return new Date(expireTime) < new Date()
  }
  const getTaskTypeName = (taskType: string) => {
    const taskTypes: Record<string, string> = {
      daily: "日常任务",
      rogue: "肉鸽任务",
      sand_fire: "生息演算",
    }
    return taskTypes[taskType] || taskType
  }

  const stageOptions = [
    { value: 'jm', label: '剿灭' },
    { value: 'ce', label: '龙门币' },
    { value: 'ls', label: '经验卡' },
    { value: 'ap', label: '红票' },
    { value: 'ca', label: '技能书' },
    { value: '1-7', label: '1-7' },
    { value: 'hd', label: '活动材料关' },
    { value: 'pr2', label: '大芯片任意' },
    { value: 'pr1', label: '小芯片任意' },
    { value: 'hd-10', label: '活动第十关' },
    { value: 'hd-9', label: '活动第九关' },
    { value: 'hd-8', label: '活动第八关' },
    { value: 'hd-7', label: '活动第七关' },
    { value: 'hd-6', label: '活动第六关' },
    { value: 'hd-5', label: '活动第五关' },
    { value: '近卫2', label: '近卫芯片[大]' },
    { value: '近卫1', label: '近卫芯片[小]' },
    { value: '特种2', label: '特种芯片[大]' },
    { value: '特种1', label: '特种芯片[小]' },
    { value: '医疗2', label: '医疗芯片[大]' },
    { value: '医疗1', label: '医疗芯片[小]' },
    { value: '重装2', label: '重装芯片[大]' },
    { value: '重装1', label: '重装芯片[小]' },
    { value: '辅助2', label: '辅助芯片[大]' },
    { value: '辅助1', label: '辅助芯片[小]' },
    { value: '狙击2', label: '狙击芯片[大]' },
    { value: '狙击1', label: '狙击芯片[小]' },
    { value: '术士2', label: '术士芯片[大]' },
    { value: '术士1', label: '术士芯片[小]' },
    { value: '先锋2', label: '先锋芯片[大]' },
    { value: '先锋1', label: '先锋芯片[小]' }
  ]

  return (
    <DashboardLayout>
      <div className="compact-page compact-config">
      <ConfigSavebar>
        <Button type="default" loading={updateLoading} onClick={handleSaveBaseConfig} disabled={loading || updateLoading || !accountInfo}>
          {updateLoading ? "保存中..." : "保存配置"}
        </Button>
      </ConfigSavebar>
      <div className="config-groups">
          {loading ? (
            <div>加载中...</div>
          ) : accountInfo ? (
            <>

              <details className="config-section" open>
<summary>基础配置<ChevronDown size={17} aria-hidden="true" /></summary>
<div className="config-body">
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {[
                      { key: "friend", label: "好友访问" },
                      { key: "credit", label: "信用商店" },
                      { key: "mail", label: "邮件收取" },
                      { key: "activity", label: "限时活动" },
                      { key: "task", label: "任务领取" },
                    ].map(item => (
                      <div key={item.key} className="flex items-center gap-3">
                        <span className="text-sm text-gray-500 text-leaf-700 w-24">{item.label}</span>
                        <Switch size="small"
                          checked={baseConfig[item.key as keyof typeof baseConfig] as boolean}
                          onChange={v => setBaseConfig(cfg => ({ ...cfg, [item.key]: v }))}
                          disabled={updateLoading}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
</details>
              <details className="config-section">
<summary>作战配置<ChevronDown size={17} aria-hidden="true" /></summary>
<div className="config-body">
  <div className="fight-enable"><span>启用作战</span><Switch size="small" checked={baseConfig.fight_enable} onChange={v=>setBaseConfig(cfg=>({...cfg,fight_enable:v}))} disabled={updateLoading}/></div>
  <FightPlanEditor value={baseConfig.fight} options={stageOptions} disabled={updateLoading} onChange={fight=>setBaseConfig(cfg=>({...cfg,fight}))}/>
  <details className="fight-sanity">
    <summary>理智补充<ChevronDown size={17} aria-hidden="true"/></summary>
    <div className="fight-sanity-fields">
      <label>源石（次/天）<input type="number" min={0} max={99} value={baseConfig.sanityStone ?? 0} onChange={e=>setBaseConfig(cfg=>({...cfg,sanityStone:Math.min(99,Math.max(0,Math.trunc(Number(e.target.value)||0)))}))} disabled={updateLoading}/></label>
      <label>药水（次/天）<input type="number" min={0} max={99} value={baseConfig.sanityDrug ?? 0} onChange={e=>setBaseConfig(cfg=>({...cfg,sanityDrug:Math.min(99,Math.max(0,Math.trunc(Number(e.target.value)||0)))}))} disabled={updateLoading}/></label>
    </div>
    <p className="fight-warning">设为 0 表示不使用。任务完成后请按需改回 0，否则后续上线仍可能消耗源石或药水。</p>
  </details>
</div>
</details>
                                          <details className="config-section">
<summary>基建配置<ChevronDown size={17} aria-hidden="true" /></summary>
<div className="config-body">
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {[
                      { key: "shift", label: "基建排班" },
                      { key: "harvest", label: "基建收获" },
                      { key: "deputy", label: "副手换人" },
                      { key: "acceleration", label: "制造站加速" },
                      { key: "communication", label: "线索交流" },
                    ].map(item => (
                      <div key={item.key} className="flex items-center gap-3">
                        <span className="text-sm text-gray-500 text-leaf-700 w-24">{item.label}</span>
                        <Switch size="small"
                          checked={baseConfig.infrastructure?.[item.key as keyof typeof baseConfig.infrastructure] ?? false}
                          onChange={v => setBaseConfig(cfg => ({
                            ...cfg,
                            infrastructure: {
                              ...cfg.infrastructure,
                              [item.key]: v
                            }
                          }))}
                          disabled={updateLoading}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
</details>
              <details className="config-section">
<summary>公招配置<ChevronDown size={17} aria-hidden="true" /></summary>
<div className="config-body">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                  {[
                    { key: "offer", label: "启用公招" },
                    { key: "car", label: "招募小车" },
                    { key: "other", label: "招募三星" },
                    { key: "star4", label: "招募四星" },
                    { key: "star5", label: "招募五星" },
                    { key: "star6", label: "招募六星" },
                  ].map(item => {
                    const offerObj = baseConfig.offer ?? accountInfo.config?.daily?.offer ?? {};
                    const checked = item.key === "offer"
                      ? (offerObj.enable ?? false)
                      : (offerObj[item.key as keyof typeof offerObj] ?? false);
                    return (
                      <div key={item.key} className="flex items-center gap-3">
                        <span className="text-sm text-gray-500 text-leaf-700 w-24">{item.label}</span>
                        <Switch size="small"
                          checked={checked}
                          onChange={v => {
                            setBaseConfig(cfg => {
                              const prevOffer = cfg.offer ?? accountInfo.config?.daily?.offer ?? {};
                              const newOffer = {
                                ...prevOffer,
                                [item.key]: v,
                              };
                              return {
                                ...cfg,
                                offer: newOffer,
                              };
                            });
                          }}
                          disabled={updateLoading}
                        />
                      </div>
                    );
                  })}
                </div>
                <p className="text-xs text-gray-500 text-leaf-700 mt-2">
                  勾选哪一项表示会自动招募哪一项，五星/六星不勾选会默认保留，可在日志中看到。
                </p>
              </div>
</details>
              {accountInfo.active && (
                <details className="config-section">
<summary>上号时间设置<ChevronDown size={17} aria-hidden="true" /></summary>
<div className="config-body">
                  <p className="compact-muted active-time-help">设置每天允许上号的时间。未添加时间段时，表示全天允许。</p>
                  <div className="active-time-list">
                    {Object.entries(accountInfo.active).map(([day, config]: [string, any]) => {
                      const key = (day + "Enable") as keyof typeof baseConfig;
                      const slots = baseConfig.activeDetails?.[day] ?? normalizeActiveSlots(config.detail);
                      const enabled = (baseConfig[key] as boolean | null) ?? config.enable;
                      return (
                        <div key={day} className="active-time-row">
                          <div className="active-time-heading">
                            <Switch size="small" id={`active_${day}`} checked={enabled} onChange={v => setBaseConfig(cfg => ({ ...cfg, [key]: v }))} disabled={updateLoading}/>
                            <label htmlFor={`active_${day}`} className="active-time-day">{activeDayNames[day]}</label>
                            <span className="active-time-label">允许上号时段</span>
                            <ActiveTimeModeSelect day={day} value={!enabled ? "none" : slots.length ? "custom" : "all"} onChange={mode => setBaseConfig(cfg => ({ ...cfg, [key]: mode !== "none", activeDetails: { ...cfg.activeDetails, [day]: mode === "custom" ? (slots.length ? slots : [defaultActiveSlot]) : [] } }))} disabled={updateLoading}/>
                          </div>
                          {enabled && slots.length > 0 && (
                            <div className="active-time-slots">
                              {slots.map((slot, index) => {
                                const [start, end] = splitActiveSlot(slot);
                                return <div className="active-time-slot" key={`${day}-${index}`}>
                                  <input type="time" aria-label={`${activeDayNames[day]}允许上号开始时间`} value={start} onChange={e => setBaseConfig(cfg => ({ ...cfg, activeDetails: { ...cfg.activeDetails, [day]: slots.map((item, i) => i === index ? `${e.target.value}-${splitActiveSlot(item)[1]}` : item) } }))} disabled={updateLoading}/>
                                  <span>至</span>
                                  <input type="time" aria-label={`${activeDayNames[day]}允许上号结束时间`} value={end} onChange={e => setBaseConfig(cfg => ({ ...cfg, activeDetails: { ...cfg.activeDetails, [day]: slots.map((item, i) => i === index ? `${splitActiveSlot(item)[0]}-${e.target.value}` : item) } }))} disabled={updateLoading}/>
                                  <button type="button" className="active-time-remove" aria-label={`删除${activeDayNames[day]}第${index + 1}个时间段`} onClick={() => setBaseConfig(cfg => ({ ...cfg, activeDetails: { ...cfg.activeDetails, [day]: slots.filter((_, i) => i !== index) } }))} disabled={updateLoading}>删除</button>
                                </div>
                              })}
                              <button type="button" className="active-time-add" onClick={() => setBaseConfig(cfg => ({ ...cfg, activeDetails: { ...cfg.activeDetails, [day]: [...slots, "18:00-20:00"] } }))} disabled={updateLoading}>＋ 添加时间段</button>
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
</details>
              )}

            </>
          ) : (
            <div>未获取到账户信息</div>
          )}
      </div>
      </div>
    </DashboardLayout>
  )
}
